import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Step } from '@/components/navigation/navigation-stepper';
import { Outlet, useLocation } from '@/util/react-router-dom';
import { TranslationKey } from './translations';
import { useTranslation } from './i18n-context';
import { useGetPatient } from '@/api/get-personal-info';
import { useLoggedUser } from './current-user-context';
import { getAge } from '@/util/helpers';
import { Loader } from '@/components/loaders/loader';
import { GetPersonalInfoResponse } from '@/api/api.schemas';

type VisitFor = 'myself' | 'surrogate';
const ProfileCreationContext = createContext<{
  aboveEighteen: boolean;
  profileTypeCompleted: boolean;
  withSurrogate: boolean;
  visitFor: VisitFor;
  steps: Step[];
  setAboveEighteen: (val: boolean) => void;
  setProfileTypeCompleted: (val: boolean) => void;
  setVisitFor: (val: VisitFor) => void;
  patient: GetPersonalInfoResponse | undefined;
} | null>(null);

export interface BaseStep {
  name: TranslationKey;
  path: string;
}

const typeStep = {
  name: 'navigators.profileCreation.typeStep',
  path: '/profile/type-selection',
} satisfies BaseStep;

const patientStep = {
  name: 'navigators.profileCreation.patientStep',
  path: '/profile/patient-info',
} satisfies BaseStep;

const surrogateStep = {
  name: 'navigators.profileCreation.surrogateStep',
  path: '/profile/surrogate',
} satisfies BaseStep;

export function ProfileCreationOutlet() {
  const [aboveEighteen, setAboveEighteen] = useState(false);
  const [profileTypeCompleted, setProfileTypeCompleted] = useState(false);
  const [visitFor, setVisitFor] = useState<VisitFor>('myself');

  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { data: patient, isFetching } = useGetPatient(user.id);

  const { pathname } = useLocation();

  useEffect(() => {
    if (!patient?.dateOfBirth) {
      return;
    }

    const age = getAge(patient.dateOfBirth);
    setAboveEighteen(age !== null && age >= 18);
  }, [patient?.dateOfBirth]);

  const withSurrogate = visitFor === 'surrogate' || !aboveEighteen;

  const steps = useMemo(() => {
    if (!patient) {
      return [];
    }

    const profileTypeStep = {
      ...typeStep,
      isCompleted: profileTypeCompleted,
    };

    const personalInfoStep = {
      ...patientStep,
      isCompleted: !!patient.mailingAddress1,
    };
    const surrogateInfoStep = {
      ...surrogateStep,
      isCompleted: !!patient.surrogate,
    };

    const steps = withSurrogate
      ? [profileTypeStep, personalInfoStep, surrogateInfoStep]
      : [profileTypeStep, personalInfoStep];

    return steps.map((s) => ({
      ...s,
      name: t(s.name),
      isActive: pathname === s.path,
    }));
  }, [pathname, patient, t, profileTypeCompleted, withSurrogate]);

  if (isFetching) {
    return <Loader type="large" />;
  }

  return (
    <ProfileCreationContext.Provider
      value={{
        aboveEighteen,
        steps,
        profileTypeCompleted,
        visitFor,
        withSurrogate,
        setAboveEighteen,
        setProfileTypeCompleted,
        setVisitFor,
        patient,
      }}
    >
      <Outlet />
    </ProfileCreationContext.Provider>
  );
}

export function useProfileCreation() {
  const context = useContext(ProfileCreationContext);
  if (!context) {
    throw new Error('useProfileCreation must be used within a ProfileCreationOutlet');
  }

  return context;
}
